import { useCallback, useEffect, useState } from 'react';
import { BlockerFunction, useBlocker, useLocation, useNavigate } from 'react-router';

import { useShallow } from 'zustand/react/shallow';

import { clsx } from 'clsx';

import { headerConfig } from '~/configs/headerConfig';
import { networkConfig } from '~/configs/networkConfig';
import { useHeaderConfig, useUserAgent, useValidNetwork } from '~/hooks';
import { useNavigateStore } from '~/store';

import Header from './Header';
import MenuDrawer from './MenuDrawer';

import styles from './Layout.module.scss';

/**
 * Layout 컴포넌트 props type 정의
 * @category Layout
 */
export type TypeLayoutProps = {
  /** 앱 버전 */
  appVersion: string | null;
  /** content-container 영역에 표시되는 자식 요소 */
  children: React.ReactNode;
};

/**
 * @description 레이아웃 컴포넌트
 * @param TypeLayoutProps
 * @returns - JSX.Element / Layout 컴포넌트 반환
 */
const Layout = ({ appVersion, children }: TypeLayoutProps): React.JSX.Element => {
  const navigate = useNavigate();

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const { state, pathname } = useLocation();

  const showGoBack: boolean = useHeaderConfig(headerConfig.showGoBack);
  const showDrawer: boolean = useHeaderConfig(headerConfig.showDrawer);
  const showAppLogo: boolean = useHeaderConfig(headerConfig.showAppLogo, { exact: true });
  const showClose: boolean = useHeaderConfig(headerConfig.showClose);

  const { checkValidNetwork, isValidNetwork } = useValidNetwork();
  const { isLocationPop, setIsLocationPop } = useNavigateStore(
    useShallow((state) => ({
      isLocationPop: state.isLocationPop,
      setIsLocationPop: state.actions.setIsLocationPop,
    }))
  );

  const { isAndroid, isDevMode } = useUserAgent();

  const shouldCallback: BlockerFunction = useCallback(
    (args) => {
      const { currentLocation, nextLocation } = args;
      return (
        currentLocation.pathname !== nextLocation.pathname &&
        networkConfig.includes(currentLocation.pathname) &&
        networkConfig.includes(nextLocation.pathname) &&
        !isValidNetwork
      );
    },
    [isValidNetwork]
  );

  const blocker = useBlocker(shouldCallback);

  useEffect(() => {
    const isBlocked = blocker.state === 'blocked';

    const handleBlockedState = () => {
      if (isBlocked) {
        blocker.proceed?.();
      }
    };

    const handleInvalidNetwork = async () => {
      // 네트워크 체크 페이지 또는 내부망 체크 필요 없는 페이지인 경우 처리하지 않음
      if (pathname === '/check-network' || pathname === '/before-work' || !networkConfig.includes(pathname)) {
        blocker.proceed?.();
        return;
      }

      const revalidatedNetwork = await checkValidNetwork();
      if (!revalidatedNetwork) {
        blocker.reset?.();
      } else {
        handleBlockedState();
      }
    };

    if (!isValidNetwork) {
      handleInvalidNetwork();
    } else {
      handleBlockedState();
    }
  }, [isValidNetwork, blocker]);

  useEffect(() => {
    if (isLocationPop) {
      setIsLocationPop(false);
    }
  }, [location.pathname]);

  const handleDrawerOpen = (): void => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleDrawerClose = (): void => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  /** @description 헤더의 뒤로가기 버튼 클릭 시 실행 */
  const handleClickGoBack = async (): Promise<void> => {
    setIsLocationPop(true);

    /** networkConfig에 정의된 path만 내부망 체크 */
    const shouldNetworkCheck = networkConfig.includes(location.pathname);

    if (shouldNetworkCheck) {
      const isValidNetwork = await checkValidNetwork(false);

      if (isValidNetwork) {
        const isIframePath = ['/contract/phone-certificate', '/contract/contract-sign'].includes(location.pathname);
        const isIframeNextPath = state?.isIframeNextPath ?? false;

        if (isIframePath) {
          /** 1. iframe이 사용된 경우, 해당 페이지에서 처리 */
          return;
        }

        if (isIframeNextPath && (isAndroid || isDevMode)) {
          /** 2. 이전 페이지에서 iframe이 사용되었고, 안드로이드/웹인 경우 */
          navigate(-2);
          return;
        }
      } else {
        /** 3. 내부망 연결이 되어 있지 않은 경우, 페이지 이동되지 않음 */
        return;
      }
    }

    const customGoBackPath = [
      '/submit-special-medical/preview-image',
      '/submit-special-medical/camera-result',
      '/settings/change-phone-number',
      '/monthly-timeline/register-work-plan',
      '/work-apply/change-phone-number',
    ];

    if (customGoBackPath.includes(location.pathname)) {
      /** 4. 특정 로직 실행 후 이전 페이지로 이동해야 하는 경우, 해당 페이지에서 처리 */
      return;
    }

    /** 5. 그 외의 경우 */
    navigate(-1);

    setTimeout(() => {
      setIsLocationPop(false);
    }, 0);
  };

  /** 배경색 어둡게 처리하는 페이지 */
  const isDimmedBackground = ['/before-work', '/check-network', '/start-work'].includes(pathname);

  return (
    <div className={clsx(styles['root-container'], isDimmedBackground && styles['dimmed-background'])}>
      <div className={styles['header-container']}>
        <Header
          showDrawer={showDrawer}
          showGoBack={showGoBack}
          showAppLogo={showAppLogo}
          showClose={showClose}
          onDrawerIconClick={handleDrawerOpen}
          onGoBackIconClick={handleClickGoBack}
        />
      </div>
      <div className={styles['content-container']}>{children}</div>
      <MenuDrawer appVersion={appVersion} isDrawerOpen={isDrawerOpen} onClose={handleDrawerClose} />
    </div>
  );
};

export default Layout;
